import React, { createContext, useContext, useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const location = useLocation();
  const isGarageRoute = location.pathname.startsWith('/garage');
  
  const [isDarkMode, setIsDarkMode] = useState(() => {
    // Check localStorage for saved preference, default to true (dark mode)
    const saved = localStorage.getItem('garage_theme');
    return saved ? saved === 'dark' : true;
  });

  useEffect(() => {
    // Save theme preference to localStorage
    localStorage.setItem('garage_theme', isDarkMode ? 'dark' : 'light');
    
    // Apply theme class to document ONLY for garage routes
    if (isDarkMode && isGarageRoute) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [isDarkMode, isGarageRoute]);

  const toggleTheme = () => {
    setIsDarkMode(prev => !prev);
  };

  // Theme object with all color classes
  const theme = {
    isDarkMode,
    // Background colors
    pageBg: isDarkMode ? 'bg-slate-900' : 'bg-gray-50',
    cardBg: isDarkMode ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-gray-200',
    cardBgSolid: isDarkMode ? 'bg-slate-800' : 'bg-white',
    headerBg: isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200 shadow-sm',
    inputBg: isDarkMode ? 'bg-slate-700/50 border-slate-600' : 'bg-white border-gray-300',
    hoverBg: isDarkMode ? 'hover:bg-slate-700/50' : 'hover:bg-gray-100',
    
    // Text colors
    textPrimary: isDarkMode ? 'text-white' : 'text-gray-900',
    textSecondary: isDarkMode ? 'text-slate-400' : 'text-gray-600',
    textMuted: isDarkMode ? 'text-slate-500' : 'text-gray-500',
    
    // Border colors
    border: isDarkMode ? 'border-slate-700' : 'border-gray-200',
    borderLight: isDarkMode ? 'border-slate-600' : 'border-gray-300',
    
    // Status colors
    successBg: isDarkMode ? 'bg-green-500/10 border-green-500/30' : 'bg-green-50 border-green-200',
    successText: isDarkMode ? 'text-green-400' : 'text-green-600',
    warningBg: isDarkMode ? 'bg-yellow-500/10 border-yellow-500/30' : 'bg-yellow-50 border-yellow-200',
    warningText: isDarkMode ? 'text-yellow-400' : 'text-yellow-600',
    errorBg: isDarkMode ? 'bg-red-500/10 border-red-500/30' : 'bg-red-50 border-red-200',
    errorText: isDarkMode ? 'text-red-400' : 'text-red-600',
    infoBg: isDarkMode ? 'bg-blue-500/10 border-blue-500/30' : 'bg-blue-50 border-blue-200',
    infoText: isDarkMode ? 'text-blue-400' : 'text-blue-600',
    
    // Special colors
    accentBg: isDarkMode ? 'bg-orange-500/10 border-orange-500/30' : 'bg-orange-50 border-orange-200',
    accentText: isDarkMode ? 'text-orange-400' : 'text-orange-600',
  };

  return (
    <ThemeContext.Provider value={{ isDarkMode, toggleTheme, theme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export default ThemeContext;
